import { useEffect, useRef } from 'react';
import { BarcodeScanner } from '@capacitor-community/barcode-scanner';
import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';

import { didUserGrantBarcodePermission } from 'clipsal-cortex-utils/src/common';
import { IS_NATIVE } from 'clipsal-cortex-utils/src/constants/common-constants';

export function useQRCodeScan() {
  const isInitialLoad = useRef(true);
  const toast = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    if (isInitialLoad.current) {
      // Calling prepare() to make startScan() work even faster.
      if (IS_NATIVE) BarcodeScanner.prepare();
      isInitialLoad.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showInvalidQRCodeToast = () => {
    toast({
      title: t('Common.invalid qr code'),
      description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
      status: 'error',
      isClosable: true,
    });
  };

  const scanQRCode = async () => {
    try {
      const hasPermission = await didUserGrantBarcodePermission();
      if (!hasPermission) {
        Sentry.captureMessage('No permission to use the camera to scan the QR code', {
          level: 'error',
          tags: {
            category: 'feature',
            feature: 'ev-charger',
            helper: 'useQRCodeScan',
            method: 'scanQRCode',
          },
        });
        toast({
          title: t('Common.camera permission required for qr code'),
          description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
          status: 'error',
          isClosable: true,
        });
        return '';
      }

      await BarcodeScanner.hideBackground();
      document.body.classList.add('qrscanner');

      const result = await BarcodeScanner.startScan();
      document.body.classList.remove('qrscanner');

      if (!result.hasContent) {
        showInvalidQRCodeToast();
        return '';
      }

      const codeContent = result.content;
      if (!codeContent) {
        showInvalidQRCodeToast();
        return '';
      }

      return codeContent;
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          category: 'feature',
          feature: 'ev-charger',
          helper: 'useQRCodeScan',
          method: 'scanQRCode',
        },
      });
      showInvalidQRCodeToast();
      return '';
    }
  };

  return {
    scanQRCode,
    showInvalidQRCodeToast,
  };
}
